import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, MapPin, Clock, Users, Home, Utensils, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { PartnerNudge } from "@/components/PartnerNudge";


const PROVIDER_LABEL = { outfitter: "VERIFIED OUTFITTER", ranch: "VERIFIED RANCH", guide: "VERIFIED GUIDE" };

export default function HuntDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", preferred_dates: "", hunters: 2, message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.huntListing(id).then(setData).catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.inquiry({ listing_id: id, ...form, hunters: Number(form.hunters) });
      setSent(true);
      toast.success("Inquiry sent to the provider.");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (notFound) return (
    <div className="App bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-forest">Hunt not found.</h1>
        <button onClick={() => navigate("/")} className="mt-6 bg-forest text-bone px-6 py-3 font-semibold" data-testid="detail-back-home">Back to HomeRange</button>
      </div><Footer />
    </div>
  );
  if (!data) return <div className="App bg-paper min-h-screen"><SiteHeader /><div className="py-40 text-center text-charcoal/50">Loading…</div></div>;

  const { listing, provider } = data;
  const facts = [
    { icon: MapPin, label: "Location", value: `${listing.general_area || listing.jurisdiction}` },
    { icon: Clock, label: "Duration", value: `${listing.duration_days} days` },
    { icon: Users, label: "Method", value: listing.method },
    { icon: listing.private_access ? Lock : MapPin, label: "Access", value: listing.private_access ? "Private access" : "Public land" },
    { icon: Home, label: "Lodging", value: listing.lodging_included ? "Included" : "Not included" },
    { icon: Utensils, label: "Meals", value: listing.meals_included ? "Included" : "Not included" },
  ];

  return (
    <div className="App bg-paper min-h-screen" data-testid="hunt-detail-page">
      <SiteHeader />

      <div className="relative h-[46vh] min-h-[340px] bg-forest overflow-hidden">
        {listing.image_url && <img src={listing.image_url} alt={listing.title} className="absolute inset-0 w-full h-full object-cover opacity-55" />}
        <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-transparent" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-8">
          <button onClick={() => navigate("/")} className="text-bone/80 hover:text-bone transition-colors inline-flex items-center gap-1.5 mb-4 w-fit" data-testid="detail-back"><ArrowLeft size={16} /> Back to hunts</button>
          <span className="hr-overline text-[#8fd0a6] flex items-center gap-1.5"><BadgeCheck size={14} /> {PROVIDER_LABEL[provider?.provider_type] || "VERIFIED"} · {listing.jurisdiction}</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone mt-2 leading-none tracking-tight max-w-3xl">{listing.title}</h1>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[1.5fr_1fr] gap-10">
        <div>
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            {facts.map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-bone border border-saddle/20 p-4" data-testid="detail-fact">
                <Icon size={18} className="text-saddle" />
                <span className="hr-overline text-saddle text-[9px] block mt-2">{label}</span>
                <strong className="text-forest">{value}</strong>
              </div>
            ))}
          </div>

          <h2 className="font-serif text-3xl text-forest mb-3">About this hunt</h2>
          <p className="text-charcoal/70 leading-relaxed">{listing.description || `A ${listing.duration_days}-day ${listing.method?.toLowerCase()} ${listing.species?.toLowerCase()} hunt in ${listing.general_area || listing.jurisdiction} with ${provider?.name}.`}</p>
          {listing.availability_text && (
            <div className="mt-6 bg-bone border-l-4 border-blaze p-4">
              <span className="hr-overline text-saddle">Availability</span>
              <p className="text-forest mt-1">{listing.availability_text}</p>
            </div>
          )}

          <div className="mt-8 border border-saddle/20 p-6 bg-white">
            <span className="hr-overline text-saddle">Provider</span>
            <h3 className="font-serif text-2xl text-forest mt-1 flex items-center gap-2">{provider?.name} <BadgeCheck size={18} className="text-[#326143]" /></h3>
            <p className="text-charcoal/60 mt-2">{provider?.description} Serving {provider?.general_region}.</p>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-forest text-bone p-7">
            <span className="hr-overline text-saddle">Price per hunter</span>
            <div className="font-serif text-4xl mt-1 mb-5">${(listing.price_per_hunter || 0).toLocaleString()}</div>
            {sent ? (
              <div className="bg-forest-2 p-5 text-center" data-testid="inquiry-success">
                <BadgeCheck size={30} className="text-[#8fd0a6] mx-auto" />
                <p className="mt-2 font-semibold">Inquiry sent!</p>
                <p className="text-bone/60 text-sm mt-1">{provider?.name} will follow up with availability and next steps.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="inquiry-form">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full bg-bone text-charcoal px-4 py-3 outline-none focus:ring-2 focus:ring-blaze" data-testid="inquiry-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-bone text-charcoal px-4 py-3 outline-none focus:ring-2 focus:ring-blaze" data-testid="inquiry-email" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.preferred_dates} onChange={(e) => setForm({ ...form, preferred_dates: e.target.value })} placeholder="Preferred dates" className="w-full bg-bone text-charcoal px-4 py-3 outline-none focus:ring-2 focus:ring-blaze min-w-0" data-testid="inquiry-dates" />
                  <input type="number" min="1" value={form.hunters} onChange={(e) => setForm({ ...form, hunters: e.target.value })} placeholder="Hunters" className="w-full bg-bone text-charcoal px-4 py-3 outline-none focus:ring-2 focus:ring-blaze min-w-0" data-testid="inquiry-hunters" />
                </div>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell the provider about your hunt…" rows={3} className="w-full bg-bone text-charcoal px-4 py-3 outline-none focus:ring-2 focus:ring-blaze resize-none" data-testid="inquiry-message" />
                {err && <p className="text-[#ffb08a] text-sm">{err}</p>}
                <button type="submit" disabled={busy} className="w-full bg-blaze text-white py-3.5 font-semibold hover:bg-blaze-dark transition-colors disabled:opacity-50" data-testid="inquiry-submit">
                  {busy ? "Sending…" : "Request Availability"}
                </button>
                <p className="text-bone/40 text-xs text-center">No payment now — this sends an inquiry to the provider.</p>
              </form>
            )}
          </div>
        </aside>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pb-10">
        <PartnerNudge state={listing.jurisdiction} preferType="outfitter" label="HomeRange partner near this hunt" />
      </div>

      <Footer />
    </div>
  );
}
